/**
 * Replace raster custom-logo `<image>` elements in a composed production SVG
 * with true vector artwork, so factories receive a fully vector file.
 *
 * The composite always embeds custom logos as base64 `<image>` elements (the
 * generator rasterizes every custom logo through a canvas before placing it),
 * so the original storage keys cannot be recovered from the SVG itself — the
 * caller passes them in add-order (`ComposeResult.customLogoKeys`), which
 * matches document order of the non-pattern `<image>` elements.
 *
 * Vector sources:
 *  - `.svg` logos: the original file is fetched from storage and inlined.
 *  - raster logos: traced server-side by the existing
 *    `GET /api/download/vectorized-logo?path={key}&format=svg&generate=1`
 *    endpoint (vectorizer.ai behind an S3 cache; a designer-uploaded manual
 *    vector outranks the auto-trace). On dev/staging the tracer runs in test
 *    mode and watermarks the output — geometry is still correct.
 *
 * Placement is preserved exactly: the `<image>` box is kept and the vector is
 * nested as `<svg x y width height viewBox preserveAspectRatio="none">`, ids
 * namespaced per logo. Failures keep the base64 raster (file stays portable)
 * and are reported as warnings.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

export interface InlineLogoVectorsOptions {
  /** CDN/storage prefix (with trailing slash) used to fetch original .svg logos. */
  storageUrl: string;
  /** Builds the backend auto-trace URL for a raster logo's storage key. */
  vectorizeUrl: (s3Key: string) => string;
  fetchImpl?: typeof fetch;
  /** Shared across a batch: `key|url` -> raw fetched SVG text (null = known failure). */
  cache?: Map<string, string | null>;
  /**
   * Per-logo choice of where the vector comes from (see logoSources.ts): a URL
   * to fetch, `null` to keep the embedded raster (staff chose the uploaded
   * file), `undefined` for the default — the original when it is an .svg, else
   * the auto-trace endpoint.
   */
  resolveUrl?: (s3Key: string) => string | null | undefined;
}

export interface InlineLogoVectorsResult {
  svg: string;
  replacedCount: number;
  warnings: string[];
}

/** Strip query string and any leading slash so keys compare/join cleanly. */
export function normalizeStorageKey(url: string): string {
  return url.split('?')[0].replace(/^\/+/, '').trim();
}

export function isSvgKey(key: string): boolean {
  return /\.svg$/i.test(normalizeStorageKey(key));
}

/** True when the fetched body looks like a standalone SVG document (not an HTML loader page). */
export function looksLikeSvgDocument(text: string): boolean {
  if (!text) return false;
  if (/<html[\s>]/i.test(text)) return false;
  return /<svg[\s>]/i.test(text);
}

export interface ExtractedSvgContent {
  viewBox: string;
  inner: string;
}

/** Pull the root viewBox (or synthesize one from width/height) and the inner markup. */
export function extractSvgContent(svgText: string): ExtractedSvgContent | null {
  const openMatch = svgText.match(/<svg\b[^>]*>/i);
  if (!openMatch || openMatch.index === undefined) return null;
  const openTag = openMatch[0];

  const innerStart = openMatch.index + openTag.length;
  const closeIndex = svgText.lastIndexOf('</svg>');
  if (closeIndex <= innerStart) return null;
  const inner = svgText.slice(innerStart, closeIndex).trim();
  if (!inner) return null;

  const viewBoxMatch = openTag.match(/viewBox\s*=\s*"([^"]+)"/i);
  if (viewBoxMatch) return { viewBox: viewBoxMatch[1].trim(), inner };

  const width = parseFloat(openTag.match(/\bwidth\s*=\s*"([^"]+)"/i)?.[1] ?? '');
  const height = parseFloat(openTag.match(/\bheight\s*=\s*"([^"]+)"/i)?.[1] ?? '');
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { viewBox: `0 0 ${width} ${height}`, inner };
  }
  return null;
}

/** Prefix every id (and reference to it) so multiple inlined logos can't collide. */
export function namespaceSvgIds(markup: string, prefix: string): string {
  return markup
    .replace(/\bid\s*=\s*"([^"]+)"/g, (_m, id: string) => `id="${prefix}${id}"`)
    .replace(/url\(#([^)]+)\)/g, (_m, id: string) => `url(#${prefix}${id})`)
    .replace(
      /\b(xlink:href|href)\s*=\s*"#([^"]+)"/g,
      (_m, attr: string, id: string) => `${attr}="#${prefix}${id}"`
    );
}

function isInsidePattern(el: Element): boolean {
  let parent = el.parentElement;
  while (parent) {
    if (parent.tagName.toLowerCase() === 'pattern') return true;
    parent = parent.parentElement;
  }
  return false;
}

function imageHref(el: Element): string {
  return (
    el.getAttribute('href') ??
    el.getAttributeNS(XLINK_NS, 'href') ??
    el.getAttribute('xlink:href') ??
    ''
  );
}

async function fetchLogoSvg(
  key: string,
  opts: InlineLogoVectorsOptions,
  warnings: string[]
): Promise<string | null> {
  const override = opts.resolveUrl?.(key);
  if (override === null) return null;
  const url = override ?? (isSvgKey(key) ? `${opts.storageUrl}${key}` : opts.vectorizeUrl(key));

  // Keyed by URL too: a changed production-source choice must not reuse the
  // svg fetched for the previous one within the same session.
  const cache = opts.cache;
  const cacheKey = `${key}|${url}`;
  if (cache && cache.has(cacheKey)) return cache.get(cacheKey) ?? null;

  const fetchImpl = opts.fetchImpl ?? fetch;
  let text: string | null = null;
  try {
    const response = await fetchImpl(url);
    if (response.ok) {
      const body = await response.text();
      if (looksLikeSvgDocument(body)) text = body;
      else warnings.push(`logo-vectorize-invalid-response:${key}`);
    } else {
      warnings.push(`logo-vectorize-http-${response.status}:${key}`);
    }
  } catch {
    warnings.push(`logo-vectorize-fetch-failed:${key}`);
  }
  cache?.set(cacheKey, text);
  return text;
}

export async function inlineCustomLogoVectors(
  compositeSvg: string,
  customLogoKeys: string[],
  opts: InlineLogoVectorsOptions
): Promise<InlineLogoVectorsResult> {
  const warnings: string[] = [];
  if (!customLogoKeys.length) return { svg: compositeSvg, replacedCount: 0, warnings };

  const doc = new DOMParser().parseFromString(compositeSvg, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length) {
    return { svg: compositeSvg, replacedCount: 0, warnings: ['logo-vectorize-parse-error'] };
  }

  // Custom logos are the base64 <image> elements outside <pattern> defs, in
  // z/add order — the same order the engine recorded their storage keys.
  const candidates = Array.from(doc.getElementsByTagName('image')).filter(
    (el) => !isInsidePattern(el) && imageHref(el).startsWith('data:')
  );

  if (candidates.length !== customLogoKeys.length) {
    warnings.push(
      `logo-image-count-mismatch:images=${candidates.length},keys=${customLogoKeys.length}`
    );
  }

  const pairCount = Math.min(candidates.length, customLogoKeys.length);
  let replacedCount = 0;

  for (let i = 0; i < pairCount; i++) {
    const imageEl = candidates[i];
    const key = normalizeStorageKey(customLogoKeys[i]);
    if (!key) continue;

    const svgText = await fetchLogoSvg(key, opts, warnings);
    if (!svgText) continue;

    const extracted = extractSvgContent(svgText);
    if (!extracted) {
      warnings.push(`logo-vectorize-unusable-svg:${key}`);
      continue;
    }

    const width = parseFloat(imageEl.getAttribute('width') ?? '');
    const height = parseFloat(imageEl.getAttribute('height') ?? '');
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      warnings.push(`logo-vectorize-missing-box:${key}`);
      continue;
    }

    const vb = extracted.viewBox
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (vb.length !== 4 || vb.some((n) => !Number.isFinite(n)) || vb[2] <= 0 || vb[3] <= 0) {
      warnings.push(`logo-vectorize-unusable-svg:${key}`);
      continue;
    }

    // Replicate the raster's box with a plain transform group (equivalent to
    // a nested <svg preserveAspectRatio="none"> viewport, which CorelDRAW's
    // SVG import silently drops). The traced content fills its viewBox
    // exactly, so no clipping is needed.
    const x = parseFloat(imageEl.getAttribute('x') ?? '0') || 0;
    const y = parseFloat(imageEl.getAttribute('y') ?? '0') || 0;
    const sx = width / vb[2];
    const sy = height / vb[3];
    const nested = doc.createElementNS(SVG_NS, 'g');
    nested.setAttribute(
      'transform',
      `translate(${x} ${y}) scale(${sx} ${sy}) translate(${-vb[0]} ${-vb[1]})`
    );
    nested.innerHTML = namespaceSvgIds(extracted.inner, `plogo${i}_`);

    // fabric puts the placement transform on the wrapping <g>, but keep any
    // transform that sits on the <image> itself (nested <svg> ignores it).
    const transform = imageEl.getAttribute('transform');
    if (transform) {
      const wrapper = doc.createElementNS(SVG_NS, 'g');
      wrapper.setAttribute('transform', transform);
      wrapper.appendChild(nested);
      imageEl.replaceWith(wrapper);
    } else {
      imageEl.replaceWith(nested);
    }
    replacedCount++;
  }

  return { svg: new XMLSerializer().serializeToString(doc), replacedCount, warnings };
}
